import Component from '@glimmer/component';
import { t } from 'ember-intl';
import Button from 'tio-ui/components/button';
import { PhShieldCheck, PhWarning } from 'ember-phosphor-icons';
import AlertBanner from 'tio-ui/components/alert-banner';

interface SubmissionGuardSignature {
  Args: {
    /** Whether required documents have been uploaded */
    hasRequiredAttachments: boolean;
    /** Whether required course grades have been entered (when applicable) */
    hasRequiredGrades?: boolean;
    /** Whether all conditions/agreements have been accepted */
    hasAgreedConditions?: boolean;
    /** Whether the application has at least one course */
    hasCourses?: boolean;
    /** Whether all required fields are filled */
    hasRequiredFields?: boolean;
    /** Handler called when submit button is clicked */
    onSubmit: () => void;
    /** Whether submission is currently in progress */
    isSubmitting?: boolean;
    /** Label for the submit button */
    submitLabel?: string;
  };
  Element: HTMLDivElement;
}

/**
 * SubmissionGuard - Hard guard preventing submission without required items
 *
 * Wraps the submit action with validation checks. Shows specific guidance
 * about what is missing when the user cannot submit.
 *
 * @example
 * ```gts
 * <SubmissionGuard
 *   @hasRequiredAttachments={{this.application.hasRequiredEvidenceAttachments}}
 *   @hasRequiredGrades={{true}}
 *   @hasAgreedConditions={{this.hasAgreed}}
 *   @hasCourses={{true}}
 *   @hasRequiredFields={{true}}
 *   @onSubmit={{this.submitApplication}}
 *   @isSubmitting={{this.isSubmitting}}
 * />
 * ```
 */
export default class SubmissionGuard extends Component<SubmissionGuardSignature> {
  get canSubmit() {
    const {
      hasRequiredAttachments,
      hasRequiredGrades = true,
      hasAgreedConditions = true,
      hasCourses = true,
      hasRequiredFields = true,
    } = this.args;

    return (
      hasRequiredAttachments &&
      hasRequiredGrades &&
      hasAgreedConditions &&
      hasCourses &&
      hasRequiredFields
    );
  }

  get missingItems(): string[] {
    const items: string[] = [];
    if (!this.args.hasCourses && this.args.hasCourses !== undefined) {
      items.push('tuition_assistance.reimagined.wizard.guard.missing_courses');
    }
    if (!this.args.hasRequiredAttachments) {
      items.push('tuition_assistance.reimagined.wizard.guard.missing_documents');
    }
    if (!this.args.hasRequiredGrades && this.args.hasRequiredGrades !== undefined) {
      items.push('tuition_assistance.reimagined.wizard.guard.missing_grades');
    }
    if (!this.args.hasAgreedConditions && this.args.hasAgreedConditions !== undefined) {
      items.push('tuition_assistance.reimagined.wizard.guard.missing_conditions');
    }
    if (!this.args.hasRequiredFields && this.args.hasRequiredFields !== undefined) {
      items.push('tuition_assistance.reimagined.wizard.guard.missing_fields');
    }
    return items;
  }

  <template>
    <div class="space-y-4" ...attributes>
      {{#if this.canSubmit}}
        <AlertBanner @intent="success">
          {{t "tuition_assistance.reimagined.wizard.guard.ready_message"}}
        </AlertBanner>
      {{else}}
        <AlertBanner
          @intent="warning"
          @title={{t "tuition_assistance.reimagined.wizard.guard.blocked_title"}}
        >
          <ul class="list-disc list-inside space-y-1 mt-1">
            {{#each this.missingItems as |itemKey|}}
              <li>{{t itemKey}}</li>
            {{/each}}
          </ul>
        </AlertBanner>
      {{/if}}

      <Button
        @intent="primary"
        @appearance="default"
        disabled={{if this.canSubmit false true}}
        {{on "click" @onSubmit}}
        class="w-full justify-center"
        data-test-reimagined-wizard-submit-button
      >
        {{#if @isSubmitting}}
          <span class="tio-row-tight">
            <span class="tio-tas-submit-spinner animate-spin" />
            {{t "tuition_assistance.reimagined.wizard.guard.submitting"}}
          </span>
        {{else if this.canSubmit}}
          <span class="tio-row-tight">
            <PhShieldCheck @size={{18}} />
            {{#if @submitLabel}}
              {{@submitLabel}}
            {{else}}
              {{t "tuition_assistance.reimagined.wizard.guard.submit_button"}}
            {{/if}}
          </span>
        {{else}}
          <span class="tio-row-tight">
            <PhWarning @size={{18}} />
            {{t "tuition_assistance.reimagined.wizard.guard.submit_blocked"}}
          </span>
        {{/if}}
      </Button>
    </div>
  </template>
}
